// The interior tutorial's practice room: a room that exists only in this browser
// (PRACTICE_ROOM_ID). A new owner furnishes it step by step — practice purchases
// cost nothing and never reach the server — and at the end is taken into their
// real house. Only the outcome (done / skipped) is stored on the server.
import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import posthog from 'posthog-js';
import {
  addPracticePurchase,
  nextStep,
  PRACTICE_ROOM_ID,
  PRACTICE_SPAWN,
  TUTORIAL_DONE,
  TUTORIAL_SKIPPED,
  tutorialPending,
  type TutorialStep,
} from '../../../domain/interiorTutorial';
import type { FurnitureCatalog, HouseFurniture } from '../../../models/apiTypes';
import useInterfaceStore from '../../../stores/useInterfaceStore';
import { patchOnboardingStateOnServer, type OnboardingState } from '../../../utils/net/onboardingApi';
import { useUserData } from '../../../hooks/useUserData';

interface PracticeRoomState {
  /** The player's real house — where practice leads at the end. null: no practice running. */
  realHouseId: number | null;
  ownerId: number | null;
  step: TutorialStep;
  /** Right after a finished tutorial the real room's Shop / Inventory glow. */
  glowShop: boolean;
  /** "Skip the tutorial?" is showing (asked by ✕ or a back button). */
  skipAsked: boolean;
  /** The tutorial just ended: on leaving the house the map shows "Don't get lost!" once. */
  postBuyMessagePending: boolean;
}

export const usePracticeRoom = create<PracticeRoomState>(() => ({
  realHouseId: null,
  ownerId: null,
  step: 'build',
  glowShop: false,
  skipAsked: false,
  postBuyMessagePending: false,
}));

/** ✕ / back during the tutorial: ask before leaving it. */
export const askSkipPractice = (asked = true) => usePracticeRoom.setState({ skipAsked: asked });

const PRACTICE_KEY = ['houseFurniture', PRACTICE_ROOM_ID] as const;
/** Practice item ids count down from -1 (try-on previews use ≤ -100000). */
let nextPracticeId = -1;
/** How long the real room's Shop / Inventory glow once they are on screen. */
export const SHOP_GLOW_MS = 6000;

const track = (step: string) => posthog.capture('interior_onboarding_step', { step });

export function startPractice(queryClient: QueryClient, realHouseId: number, ownerId: number | null): void {
  nextPracticeId = -1;
  queryClient.setQueryData<HouseFurniture[]>(PRACTICE_KEY, []);
  usePracticeRoom.setState({ realHouseId, ownerId, step: 'build', glowShop: false, skipAsked: false });
  track('build');
  useInterfaceStore.getState().setRoomView(PRACTICE_ROOM_ID, ownerId);
}

/** A practice purchase: the item appears in the practice room, nothing is charged. */
export function practiceBuy(queryClient: QueryClient, catalog: FurnitureCatalog): void {
  const item: HouseFurniture = {
    id: nextPracticeId--,
    map_object_id: PRACTICE_ROOM_ID,
    furniture_catalog_id: catalog.id,
    furniture_catalog: catalog,
    is_placed: true,
    room_index: 0,
    x: PRACTICE_SPAWN.x,
    y: PRACTICE_SPAWN.y,
    is_mirrored: false,
    placement_mode: 'floor',
    purchased_by: 0,
    purchased_at: new Date().toISOString(),
  };
  queryClient.setQueryData<HouseFurniture[]>(PRACTICE_KEY, (old = []) => addPracticePurchase(old, item));
}

/** Move on from `from` (ignored if the tutorial already left it — completion checks can repeat). */
export function advancePractice(from: TutorialStep): void {
  const { step, realHouseId } = usePracticeRoom.getState();
  if (realHouseId === null || step !== from) return;
  const next = nextStep(step);
  if (next === TUTORIAL_DONE) return; // the finish card is closed by the player
  usePracticeRoom.setState({ step: next });
  track(next);
}

/** Leave practice for the real house, finished or skipped; the outcome is stored for good. */
export function endPractice(queryClient: QueryClient, outcome: typeof TUTORIAL_DONE | typeof TUTORIAL_SKIPPED): void {
  const { realHouseId, ownerId } = usePracticeRoom.getState();
  queryClient.setQueryData<OnboardingState>(['onboarding-state'], (old) => (old ? { ...old, interior_onboarding: outcome } : old));
  patchOnboardingStateOnServer({ interior_onboarding: outcome }).catch(() => { /* re-shown next entry at worst */ });
  track(outcome);
  usePracticeRoom.setState({
    realHouseId: null,
    ownerId: null,
    step: 'build',
    glowShop: outcome === TUTORIAL_DONE,
    skipAsked: false,
    postBuyMessagePending: true,
  });
  queryClient.removeQueries({ queryKey: PRACTICE_KEY });
  const ui = useInterfaceStore.getState();
  if (realHouseId === null) {
    ui.setMapView();
    return;
  }
  ui.setRoomView(realHouseId, ownerId);
  // Finished: straight into furnishing — build mode on, Shop / Inventory glowing.
  if (outcome === TUTORIAL_DONE) ui.setRoomBuildMode(true);
}

/**
 * "Enter house": a new owner who has not had the interior tutorial goes to the
 * practice room first; everyone else straight into the house.
 */
export function useEnterHouse(): (houseId: number, ownerId: number | null) => void {
  const queryClient = useQueryClient();
  const { data: userData } = useUserData();
  return useCallback((houseId, ownerId) => {
    const onboarding = queryClient.getQueryData<OnboardingState>(['onboarding-state']);
    const ownHouse = !!userData && ownerId !== null && String(userData.user_id) === String(ownerId);
    if (ownHouse && onboarding?.onboarding_completed && tutorialPending(onboarding.interior_onboarding)) {
      startPractice(queryClient, houseId, ownerId);
    } else {
      useInterfaceStore.getState().setRoomView(houseId, ownerId);
    }
  }, [queryClient, userData]);
}
